import React, { useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { ListItemWebsite, DomainsChipDownContainer } from "@similarweb/ui-components";

import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { useLoading } from "app/custom-hooks/loadingHook";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    BoxContainer,
    FlexRowAlignCenter,
    GreyNotificationContainer,
    WebsiteAnalysisSubdomainsContainer,
} from "app/pages/website-analysis/subdomains/StyledComponents";
import { SubdomainsTable } from "app/pages/website-analysis/subdomains/SubdomainsTable";
import type { IWebAnalysisSubdomainsExcelRequestParams } from "app/pages/website-analysis/subdomains/WebAnalysisSubdomainsApiService";
import SubdomainsApiService from "app/pages/website-analysis/subdomains/WebAnalysisSubdomainsApiService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

const GreyNotificationMessage = ({ messageKey = "" }) => (
    <GreyNotificationContainer>
        <SWReactIcons
            iconName="info"
            size="xs"
        />
        <span>{i18nFilter()(messageKey)}</span>
    </GreyNotificationContainer>
);

const WebAnalysisSubdomainsContainer = () => {
    const services = React.useMemo(
        () => ({
            swNavigator: proNavigator,
            i18n: i18nFilter(),
        }),
        [],
    );
    const chosenSites = useSelector((state: RootState) => {
        return state?.routing?.chosenItems;
    });
    const params = services.swNavigator.getParams() as any;
    const apiParams = services.swNavigator.getApiParams(params);
    const { subdomainsApiService } = React.useMemo(
        () => ({
            subdomainsApiService: new SubdomainsApiService(),
        }),
        [],
    );
    const [siteList, setSiteList] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState<{
        name: string;
        icon: string;
    }>(() => {
        const domainArr = params?.key?.split(",");
        return { name: domainArr[0], icon: "" };
    });
    const [tableData, tableDataOps] = useLoading();
    const isLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(tableData.state);
    const renderNoData = () => {
        return (
            <TableNoData
                icon="no-data"
                messageTitle={services.i18n("global.nodata.notavilable")}
            />
        );
    };

    const isCompare = React.useMemo(() => {
        return params?.key?.split(",").length > 1;
    }, [params.key]);

    React.useEffect(() => {
        tableDataOps.load(() => {
            const {
                from,
                to,
                timeGranularity = "Monthly",
                webSource = "Desktop",
                includeSubDomains = params.isWWW === "*",
                isWindow,
                orderBy,
                country,
                key,
            } = apiParams;
            return subdomainsApiService.getDomainsSubdomainsTableData({
                from,
                to,
                timeGranularity,
                webSource,
                includeSubDomains,
                isWindow,
                orderBy,
                country,
                key: selectedDomain.name ? selectedDomain.name : key,
            } as any);
        });
    }, [selectedDomain.name]);

    React.useEffect(() => {
        const { name, icon } = chosenSites?.[0];
        setSelectedDomain({ name, icon });
        setSiteList(chosenSites);
    }, [chosenSites]);

    const tableExcelLink = useMemo(() => {
        const {
            from,
            to,
            timeGranularity = "Monthly",
            webSource = "Desktop",
            includeSubDomains = params.isWWW === "*",
            isWindow,
            orderBy,
            country,
            key,
        } = apiParams;
        return subdomainsApiService.getDomainsSubdomainsTableExcelUrl({
            from,
            to,
            timeGranularity,
            webSource,
            includeSubDomains,
            orderBy,
            country,
            keys: key,
            fileName: `Website Analysis Subdomains-(${key})-(from: ${from})-(to: ${to})`,
            isWindow,
        } as IWebAnalysisSubdomainsExcelRequestParams);
    }, [apiParams]);

    useEffect(() => {
        return () => {
            A2DRef.current?.dismiss();
        };
    }, []);
    const A2DRef = useRef(null);

    const getDomainsOptions = () => {
        return siteList.map(({ name, icon }, index) => {
            return (
                <ListItemWebsite
                    key={index}
                    text={name}
                    img={icon}
                />
            );
        });
    };

    const onClick = React.useCallback((selectedDomain) => {
        const newSelectedDomain = {
            icon: selectedDomain.img,
            name: selectedDomain.text,
        };
        setSelectedDomain(newSelectedDomain);
    }, []);

    // this function was added because DomainsChipDownContainer component require it, it is not being used/called and can be removed in the future
    const onClose = React.useCallback((selectedDomain) => {
        const newSelectedDomain = {
            icon: selectedDomain.img,
            name: selectedDomain.text,
        };
        setSelectedDomain(newSelectedDomain);
    }, []);

    return (
        <WebsiteAnalysisSubdomainsContainer>
            <BoxContainer data-automation-website-analysis-subdomains={true}>
                {!isLoading && !tableData ? renderNoData() : null}
                {isCompare ? (
                    <FlexRowAlignCenter>
                        <DomainsChipDownContainer
                            width={200}
                            onClick={onClick}
                            selectedDomainText={selectedDomain.name}
                            selectedDomainIcon={selectedDomain.icon}
                            onCloseItem={onClose}
                            buttonText=""
                            noResultsMessage={services.i18n("dropdown.no.results")}
                        >
                            {getDomainsOptions()}
                        </DomainsChipDownContainer>
                    </FlexRowAlignCenter>
                ) : null}
                <SubdomainsTable
                    isCompare={isCompare}
                    tableData={tableData.data}
                    isLoading={isLoading}
                    tableExcelUrl={tableData ? tableExcelLink : null}
                />
            </BoxContainer>
        </WebsiteAnalysisSubdomainsContainer>
    );
};

export default WebAnalysisSubdomainsContainer;
